import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, UserPlus, Upload, Search, Trash2, Eye, X, Repeat, Mail, Heart, MessageSquare } from "lucide-react";

const STATUS = { lead: { label: "Lead", cls: "bg-amber-100 text-amber-800" }, active: { label: "Active", cls: "bg-emerald-100 text-emerald-800" }, inactive: { label: "Inactive", cls: "bg-muted text-muted-foreground" } };
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const ix = { name: header.indexOf("name"), email: header.indexOf("email"), phone: header.indexOf("phone"), notes: header.indexOf("notes") };
  const at = (cols, i) => (i >= 0 && cols[i] != null ? cols[i].trim() : "");
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(",");
    const email = at(cols, ix.email);
    if (!email) continue;
    rows.push({ name: at(cols, ix.name), email, phone: at(cols, ix.phone), notes: at(cols, ix.notes) });
  }
  return rows;
}

export const CustomersTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "", status: "active" });
  const [importRows, setImportRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const load = () => { setLoading(true); api.get("/crm/customers").then((r) => setRows(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const filtered = rows
    .filter((c) => {
      const q = search.toLowerCase();
      const matchQ = !q || (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q) || (c.phone || "").includes(q);
      const matchStatus = statusFilter === "all" || c.status === statusFilter;
      return matchQ && matchStatus;
    })
    .sort((a, b) => {
      if (sortBy === "spend") return (b.total_spent || 0) - (a.total_spent || 0);
      if (sortBy === "bookings") return (b.bookings_count || 0) - (a.bookings_count || 0);
      if (sortBy === "name") return (a.name || a.email).localeCompare(b.name || b.email);
      return (b.last_booking || "").localeCompare(a.last_booking || ""); // recent
    });

  const addCustomer = async () => {
    if (!form.email.trim()) { toast.error("Email is required."); return; }
    setSaving(true);
    try { await api.post("/crm/customers", form); toast.success("Customer saved."); setAddOpen(false); setForm({ name: "", email: "", phone: "", notes: "", status: "active" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
    finally { setSaving(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { const parsed = parseCsv(String(reader.result)); if (!parsed.length) toast.error("No valid rows found. Need a header row with an 'email' column."); setImportRows(parsed); };
    reader.readAsText(f);
  };

  const runImport = async () => {
    if (!importRows.length) { toast.error("Nothing to import."); return; }
    setSaving(true);
    try { const { data } = await api.post("/crm/customers/import", { rows: importRows }); toast.success(`Imported ${data.imported} customer(s).`); setImportOpen(false); setImportRows([]); if (fileRef.current) fileRef.current.value = ""; load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Import failed."); }
    finally { setSaving(false); }
  };

  const removeRel = async (c) => {
    if (!window.confirm(`Remove ${c.name || c.email} from your customers? This only removes them from your list — it doesn't delete the customer.`)) return;
    try { await api.delete(`/crm/customers/${c.id}`); toast.success("Removed from your customers."); load(); }
    catch (e) { toast.error("Couldn't remove."); }
  };

  return (
    <div data-testid="customers-tab">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone" className="pl-9 rounded-sm" data-testid="crm-search" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-sm w-[130px]" data-testid="crm-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="lead">Leads</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="rounded-sm w-[150px]" data-testid="crm-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Recent booking</SelectItem>
              <SelectItem value="spend">Highest spend</SelectItem>
              <SelectItem value="bookings">Most bookings</SelectItem>
              <SelectItem value="name">Name (A–Z)</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="rounded-sm" onClick={() => setImportOpen(true)} data-testid="crm-import-btn"><Upload className="w-4 h-4 mr-1.5" />Import CSV</Button>
          <Button className="rounded-sm" onClick={() => setAddOpen(true)} data-testid="crm-add-btn"><UserPlus className="w-4 h-4 mr-1.5" />Add customer</Button>
        </div>
      </div>

      {loading ? (
        <p className="text-muted-foreground mt-8">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="mt-10 text-center border border-dashed border-border rounded-sm p-10" data-testid="crm-empty">
          <Users className="w-8 h-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
          <p className="mt-3 font-medium">No customers yet</p>
          <p className="text-sm text-muted-foreground">Customers who book with you appear here automatically. You can also import or add them.</p>
        </div>
      ) : (
        <div className="mt-6 border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Bookings</TableHead>
                <TableHead className="text-right">Total spent</TableHead>
                <TableHead>Last booking</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} data-testid={`crm-row-${c.id}`}>
                  <TableCell>
                    <div className="font-medium flex items-center gap-2">
                      {c.name || "—"}
                      {c.bookings_count > 1 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-secondary/15 text-secondary flex items-center gap-1" data-testid={`repeat-badge-${c.id}`}><Repeat className="w-3 h-3" />Repeat</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
                    {c.tags?.length ? <div className="flex gap-1 mt-1 flex-wrap">{c.tags.map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-muted text-muted-foreground">{t}</span>)}</div> : null}
                  </TableCell>
                  <TableCell><Badge className={`rounded-sm ${STATUS[c.status]?.cls || STATUS.active.cls}`} variant="secondary">{STATUS[c.status]?.label || "Active"}</Badge></TableCell>
                  <TableCell className="text-right">{c.bookings_count}</TableCell>
                  <TableCell className="text-right">{money(c.total_spent)}</TableCell>
                  <TableCell>{c.last_booking || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDetail(c.id)} data-testid={`crm-view-${c.id}`}><Eye className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => removeRel(c)} data-testid={`crm-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add customer */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md rounded-sm" data-testid="crm-add-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Add customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="crm-form-name" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" data-testid="crm-form-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-sm" data-testid="crm-form-phone" /></div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="crm-form-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="lead">Lead</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-sm" data-testid="crm-form-notes" /></div>
          </div>
          <DialogFooter><Button onClick={addCustomer} disabled={saving} className="rounded-sm w-full" data-testid="crm-form-save">{saving ? "Saving…" : "Save customer"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV */}
      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportRows([]); if (fileRef.current) fileRef.current.value = ""; } }}>
        <DialogContent className="max-w-md rounded-sm" data-testid="crm-import-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Import customers</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">Upload a CSV with a header row. Columns: <b>name, email, phone, notes</b> (notes optional). Email is required.</p>
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="text-sm" data-testid="crm-import-file" />
          {importRows.length > 0 && (
            <div className="border border-border rounded-sm max-h-52 overflow-auto text-sm" data-testid="crm-import-preview">
              {importRows.map((r, i) => (
                <div key={i} className="flex justify-between px-3 py-1.5 border-b border-border last:border-0">
                  <span>{r.name || "—"} <span className="text-muted-foreground">· {r.email}</span></span>
                  <span className="text-muted-foreground">{r.phone}</span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter><Button onClick={runImport} disabled={saving || !importRows.length} className="rounded-sm w-full" data-testid="crm-import-run">{saving ? "Importing…" : `Import ${importRows.length || ""} customer(s)`}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {detail && <CustomerDetail id={detail} onClose={() => setDetail(null)} onChanged={load} />}
    </div>
  );
};

const CustomerDetail = ({ id, onClose, onChanged }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("active");
  const [tags, setTags] = useState("");
  const [saving, setSaving] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [mail, setMail] = useState({ subject: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/crm/customers/${id}`).then((r) => { setData(r.data); setNotes(r.data.notes || ""); setStatus(r.data.status || "active"); setTags((r.data.tags || []).join(", ")); });
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/crm/customers/${id}`, { notes, status, tags: tags.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("Saved."); onChanged?.();
    } catch (e) { toast.error("Couldn't save."); } finally { setSaving(false); }
  };

  const sendEmail = async () => {
    if (!mail.subject.trim() || !mail.message.trim()) { toast.error("Subject and message are required."); return; }
    setSending(true);
    try {
      const { data: res } = await api.post(`/crm/customers/${id}/email`, mail);
      toast.success(`Email sent to ${res.sent_to}.`); setEmailOpen(false); setMail({ subject: "", message: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send email."); } finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-auto" data-testid="crm-detail-dialog">
        {!data ? <p className="text-muted-foreground">Loading…</p> : (
          <>
            <DialogHeader><DialogTitle className="font-display text-2xl">{data.name || data.email}</DialogTitle></DialogHeader>
            <div className="flex items-center justify-between gap-2 -mt-2">
              <p className="text-sm text-muted-foreground">{data.email}{data.phone ? ` · ${data.phone}` : ""}</p>
              <div className="flex items-center gap-2">
                {data.customer_id && (
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => navigate(`/messages?to=${data.customer_id}`)} data-testid="crm-message-btn"><MessageSquare className="w-4 h-4 mr-1.5" />Message</Button>
                )}
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setEmailOpen(true)} data-testid="crm-email-btn"><Mail className="w-4 h-4 mr-1.5" />Email</Button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 my-2">
              <div className="border border-border rounded-sm p-3 text-center"><div className="text-2xl font-display">{data.bookings_count}</div><div className="text-xs text-muted-foreground">Bookings</div></div>
              <div className="border border-border rounded-sm p-3 text-center"><div className="text-2xl font-display">{money(data.total_spent)}</div><div className="text-xs text-muted-foreground">Total spent</div></div>
              <div className="border border-border rounded-sm p-3 text-center"><div className="text-2xl font-display capitalize">{data.status}</div><div className="text-xs text-muted-foreground">Status</div></div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div><Label>Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="crm-detail-status"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="lead">Lead</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Tags <span className="text-muted-foreground font-normal">(comma-separated)</span></Label><Input value={tags} onChange={(e) => setTags(e.target.value)} className="mt-1 rounded-sm" data-testid="crm-detail-tags" /></div>
                <div><Label>Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="mt-1 rounded-sm" data-testid="crm-detail-notes" /></div>
                <Button onClick={save} disabled={saving} className="rounded-sm w-full" data-testid="crm-detail-save">{saving ? "Saving…" : "Save details"}</Button>
              </div>
              <div>
                <Label className="text-sm">Booking & payment activity</Label>
                <div className="mt-1 border border-border rounded-sm divide-y divide-border max-h-72 overflow-auto" data-testid="crm-detail-activity">
                  {data.payments.length === 0 ? <p className="text-sm text-muted-foreground p-3">No bookings yet.</p> : data.payments.map((p) => (
                    <div key={p.booking_id} className="p-3 text-sm">
                      <div className="flex justify-between"><span className="font-medium">{p.service}</span><span>{money(p.amount)}</span></div>
                      <div className="flex justify-between text-xs text-muted-foreground mt-0.5">
                        <span>{p.date}</span>
                        <span className="capitalize">{p.payment_status}{p.payout_status ? ` · payout ${p.payout_status}` : ""}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {data.liked_services?.length > 0 && (
                  <div className="mt-3" data-testid="crm-liked-services">
                    <Label className="text-sm flex items-center gap-1"><Heart className="w-3.5 h-3.5 fill-primary text-primary" />Liked your services</Label>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {data.liked_services.map((l) => (
                        <span key={l.service_id} className="text-xs px-2 py-1 rounded-sm bg-primary/10 text-primary">{l.service_title}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
              <DialogContent className="max-w-md rounded-sm" data-testid="crm-email-dialog">
                <DialogHeader><DialogTitle className="font-display text-2xl">Email {data.name || data.email}</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground -mt-1">Sends to <b>{data.email}</b> from BookdHUB.</p>
                <div><Label>Subject</Label><Input value={mail.subject} onChange={(e) => setMail({ ...mail, subject: e.target.value })} className="mt-1 rounded-sm" data-testid="crm-email-subject" /></div>
                <div><Label>Message</Label><Textarea value={mail.message} onChange={(e) => setMail({ ...mail, message: e.target.value })} rows={6} className="mt-1 rounded-sm" data-testid="crm-email-message" /></div>
                <DialogFooter><Button onClick={sendEmail} disabled={sending} className="rounded-sm w-full" data-testid="crm-email-send">{sending ? "Sending…" : "Send email"}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
